import React, { PureComponent } from 'react'
import { get, values } from 'lodash'
import PropTypes from 'prop-types'
import moment from 'moment'

import ShipSearchField from './shipSearchField/ShipSearchField'
import ShipDisplayField from './shipdisplayfield/ShipDisplayField'
import ShipInputNumberField from './shipinputnumberfield/ShipInputNumberField'
import ShipInputSelectField from './shipinputselectfield/ShipInputSelectField'
import ShipInputTextField from './shipinputtextfield/ShipInputTextField'
import Map from '../../map/Map'
import { startDateFormatString } from '../../../js/constants'
import './ShipSelector.scss'
import LocationSelector from '../locationSelector/LocationSelector'
import { getErrorMessageForFormField } from '../../../utils/formUtils'
import { getFormattedDate } from '../../../utils/general'

export const SHIP_TYPES_TEXT = {
  'sea-ship': 'Seagoing Vessel',
  coaster: 'Coaster',
  barge: 'Barge'
}

const SHIP_TYPE_OPTIONS = ['-'].concat(values(SHIP_TYPES_TEXT))

const deriveEtaDisplayText = shipEta => {
  return shipEta && shipEta > moment().subtract(1, 'year').valueOf()
    ? moment(shipEta).format(startDateFormatString) + ' - ' + moment(shipEta).fromNow()
    : null
}

const getLockInformation = lockInformation => {
  if (lockInformation.atdTime) {
    return `ATD ${getFormattedDate(lockInformation.atdTime)}`
  }
  if (lockInformation.ataTime) {
    return `ATA ${getFormattedDate(lockInformation.ataTime)}`
  }
  if (lockInformation.etdTime) {
    return `ETD ${getFormattedDate(lockInformation.etdTime)}`
  }
  if (lockInformation.etaTime) {
    return `ETA ${getFormattedDate(lockInformation.etaTime)}`
  }
  return null
}

export default class ShipSelector extends PureComponent {
  static propTypes = {
    ship: PropTypes.object,
    shipDetailsForVisit: PropTypes.object,
    onChangeShip: PropTypes.func.isRequired,
    onChangeShipProperty: PropTypes.func.isRequired,
    errors: PropTypes.object,
    config: PropTypes.object.isRequired,
    loadingShipsDetails: PropTypes.bool.isRequired,
    authToken: PropTypes.string,
    customFetch: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    lockInformation: PropTypes.object.isRequired
  }

  render() {
    // Variables preceding to visits' ship
    const hasImoNumber = this.props.ship && this.props.ship.imo && Number(this.props.ship.imo) !== 0
    const hasEniNumber = this.props.ship && this.props.ship.eni
    const onlyHasMmsi = !hasEniNumber && !hasImoNumber
    const shipEta =
      this.props.shipDetailsForVisit && this.props.shipDetailsForVisit.eta ? this.props.shipDetailsForVisit.eta : null
    const currentShipDestination =
      this.props.shipDetailsForVisit && this.props.shipDetailsForVisit.destination
        ? this.props.shipDetailsForVisit.destination
        : null

    let shipStatus = null

    if (this.props.shipDetailsForVisit && this.props.shipDetailsForVisit.speedOverGround != null) {
      if (this.props.shipDetailsForVisit.speedOverGround > 0.3) {
        shipStatus = 'Moving'
      } else {
        shipStatus = 'Moored'
      }
    }

    const lockInformation = getLockInformation(this.props.lockInformation)

    return (
      <div className="detail-view content">
        <div className="detail-view-section ship-selector">
          <h1>Ship</h1>
          {!this.props.readOnly && this.props.ship && this.props.ship.mmsi && (
            <div className="btn-container">
              <i className="fa fa-trash" onClick={this.onDeleteShip} />
            </div>
          )}
          <div className="ship-selector-wrapper">
            <ShipSearchField
              ship={this.props.ship}
              onChangeShip={this.props.onChangeShip}
              authToken={this.props.authToken}
              globalSupport={true}
              filterGpsShips
              customFetch={this.props.customFetch}
              readOnly={this.props.readOnly}
              terminalId={this.props.config.terminalId}
            />

            <div className="ship-info-wrapper">
              <div className="ship-text">
                {!this.props.ship.mmsi ? (
                  <ShipInputTextField
                    category={'Name'}
                    name={'name'}
                    value={this.props.ship.name}
                    onChange={this.props.onChangeShipProperty}
                    error={getErrorMessageForFormField(this.props.errors, 'name')}
                    readOnly={this.props.readOnly}
                  />
                ) : (
                  <ShipDisplayField category={'Name'} value={this.props.ship.name} />
                )}

                <ShipDisplayField category={'MMSI'} value={this.props.ship.mmsi} />

                {!onlyHasMmsi ? (
                  <ShipDisplayField
                    category={`${hasImoNumber ? 'IMO' : 'ENI'}`}
                    value={`${hasImoNumber ? this.props.ship.imo : this.props.ship.eni}`}
                  />
                ) : null}

                {!this.props.ship.mmsi ? (
                  <ShipInputNumberField
                    category={'Length'}
                    name={'length'}
                    required={true}
                    value={this.props.ship.length}
                    onFormInputShipChange={this.props.onChangeShipProperty}
                    error={getErrorMessageForFormField(this.props.errors, 'length')}
                    readOnly={this.props.readOnly}
                  />
                ) : (
                  <ShipDisplayField
                    category={'Length'}
                    value={this.props.ship.length ? this.props.ship.length + ' m' : null}
                    required={true}
                    error={getErrorMessageForFormField(this.props.errors, 'length')}
                  />
                )}

                <ShipDisplayField
                  category={'Width'}
                  value={this.props.ship.width ? this.props.ship.width + ' m' : null}
                />

                <ShipDisplayField category={'ETA (from AIS)'} value={deriveEtaDisplayText(shipEta)} />

                <ShipDisplayField category={'Current destination'} value={currentShipDestination} />

                {!this.props.ship.mmsi ? (
                  <ShipInputSelectField
                    category={'Type of ship'}
                    name={'type'}
                    value={SHIP_TYPES_TEXT[this.props.ship.type] || '-'}
                    onFormInputSelectShipChange={this.handleSelectTypeOfShip}
                    options={SHIP_TYPE_OPTIONS}
                    required={true}
                    error={getErrorMessageForFormField(this.props.errors, 'type')}
                    readOnly={this.props.readOnly}
                  />
                ) : (
                  <ShipDisplayField
                    category={'Type of ship'}
                    value={this.props.ship.type || '-'}
                    required={true}
                    error={getErrorMessageForFormField(this.props.errors, 'type')}
                  />
                )}

                <ShipDisplayField category={'Status'} value={shipStatus} />

                {hasImoNumber ? (
                  <ShipDisplayField
                    category={'Vessel agent'}
                    value={get(this.props, ['shipDetailsForVisit', 'extras', 'vesselAgent'], '-')}
                  />
                ) : null}

                {lockInformation ? <ShipDisplayField category={'Lock information'} value={lockInformation} /> : null}
              </div>
              <div className="ship-photo-wrapper">{this.renderShipPhotoForVisit(this.props.ship)}</div>
            </div>

            <div className="ship-location-select detail-view double-view">
              <LocationSelector
                name={'Origin'}
                fieldName={'origin'}
                location={this.props.ship.origin}
                locations={this.props.config.originLocations}
                required={false}
                error={null}
                onChange={this.props.onChangeShipProperty}
                readOnly={this.props.readOnly}
              />

              <LocationSelector
                name={'Destination'}
                fieldName={'destination'}
                location={this.props.ship.destination}
                locations={this.props.config.destinationLocations}
                required={false}
                error={null}
                onChange={this.props.onChangeShipProperty}
                readOnly={this.props.readOnly}
                alignRight
              />
            </div>
          </div>
          {this.props.ship.mmsi ? (
            <Map
              config={this.props.config}
              loadingShipsDetails={this.props.loadingShipsDetails}
              selectedShip={this.props.shipDetailsForVisit}
            />
          ) : null}
        </div>
      </div>
    )
  }

  /**
   * Render ship photo
   * @param shipForVisit
   * @returns {*}
   */
  renderShipPhotoForVisit = shipForVisit => {
    if (!shipForVisit.mmsi) {
      return null
    }

    return (
      <img
        className="ship-photo"
        alt="ship"
        // @ts-ignore
        onLoad={event => (event.target.className = 'ship-photo')}
        // @ts-ignore
        onError={event => (event.target.className = 'ship-photo hide')}
        src={`https://photos.marinetraffic.com/ais/showphoto.aspx?mmsi=${shipForVisit.mmsi}`}
      />
    )
  }

  /**
   * When user deletes the ship from the terminal visit
   */
  onDeleteShip = () => this.props.onChangeShip({}, {})

  handleSelectTypeOfShip = async value => {
    const type = value !== '-' ? Object.keys(SHIP_TYPES_TEXT).find(type => SHIP_TYPES_TEXT[type] === value) : undefined

    this.props.onChangeShipProperty(type, 'type')
  }
}
